import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Separator } from './ui/separator';
import { Button } from './ui/button';
import { Scales as Scale, Info, DownloadSimple as Download, BracketsCurly } from 'phosphor-react';
import { calculateBridgeWeight } from '../data/mockData';
import { formatWeight, formatLength } from '../utils/units';

export const WeightEstimator = ({ configuration, units = 'imperial' }) => {
  const w = calculateBridgeWeight(configuration);

  const breakdown = [
    { label: 'Structure', amount: w.structureWeight },
    { label: 'Decking', amount: w.deckWeight },
    { label: 'Railings', amount: w.railingWeight },
  ];
  if (w.addOnsWeight > 0) breakdown.push({ label: 'Add-Ons', amount: w.addOnsWeight });

  const fmt = (lb) => formatWeight(lb, units);

  const exportCSV = () => {
    const header = ['Category', units === 'metric' ? 'Weight (kg)' : 'Weight (lb)'];
    const kg = (lb) => Math.round(lb * 0.45359237);
    const data = breakdown.map(r => [r.label, units === 'metric' ? kg(r.amount) : r.amount]);
    data.push(['Total (Bridge only)', units === 'metric' ? kg(w.totalWeight) : w.totalWeight]);
    if (w.wingwallsWeight > 0) data.push(['Wingwalls (separate)', units === 'metric' ? kg(w.wingwallsWeight) : w.wingwallsWeight]);
    const csv = [header, ...data].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const ts = new Date().toISOString().replace(/[:.]/g, '-');
    a.href = url;
    a.download = `bridge-weight-${configuration.trussType}-${configuration.length}x${configuration.width}-${ts}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const exportJSON = () => {
    const payload = {
      meta: {
        trussType: configuration.trussType,
        material: configuration.material,
        decking: configuration.decking,
        railing: configuration.railing,
        length: formatLength(configuration.length, units),
        width: formatLength(configuration.width, units),
      },
      weights: {
        total: fmt(w.totalWeight).main,
        breakdown: breakdown.map(b => ({ label: b.label, amount: fmt(b.amount).main }))
      }
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const ts = new Date().toISOString().replace(/[:.]/g, '-');
    a.href = url;
    a.download = `bridge-weight-${configuration.trussType}-${configuration.length}x${configuration.width}-${ts}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Card className="border-slate-600 bg-slate-800 shadow-xl">
      <CardHeader className="pb-3">
        <CardTitle className="text-slate-100 flex items-center gap-2">
          <Scale className="w-5 h-5 text-blue-400" />
          Estimated Bridge Weight
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="bg-gradient-to-r from-blue-900/20 to-purple-900/20 border border-blue-500/20 rounded-lg p-4">
          <div className="text-slate-300 text-sm">Approximate Total</div>
          <div className="flex items-end gap-3 mt-1">
            <div className="text-2xl font-bold text-slate-100">{fmt(w.totalWeight).main}</div>
            <div className="text-slate-400">{fmt(w.totalWeight).aux}</div>
          </div>
          <div className="text-slate-400 text-xs mt-1">For {formatLength(configuration.length, units)} × {formatLength(configuration.width, units)} {configuration.material} {configuration.trussType}{configuration?.subModifiers?.loadRating ? ` • Load: ${(window.__LR__ || []).find?.(o => o.id === configuration.subModifiers.loadRating)?.name || ''}` : ''}</div>
        </div>

        <div>
          <h4 className="text-slate-200 text-sm font-medium mb-2">Weight Breakdown</h4>
          <div className="space-y-2">
            {breakdown.map((b, i) => (
              <div key={i} className="flex justify-between items-center">
                <div className="text-slate-300 text-sm">{b.label}</div>
                <div className="text-slate-200">{fmt(b.amount).main}</div>
              </div>
            ))}
          </div>
        </div>

        <Separator className="bg-slate-600" />

        <div className="flex flex-col sm:flex-row gap-2">
          <Button onClick={exportCSV} className="bg-slate-700 hover:bg-slate-600 text-slate-200 border border-slate-600">
            <Download className="w-4 h-4 mr-2" /> CSV
          </Button>
          <Button onClick={exportJSON} variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-700">
            <BracketsCurly className="w-4 h-4 mr-2" /> JSON
          </Button>
        </div>

        <div className="flex items-start gap-2 text-xs text-slate-400">
          <Info className="w-4 h-4 mt-0.5 text-slate-500" />
          <div>
            This is a rough guesstimate for planning only. Actual weight varies with member sizing, spacing, and details. We can refine this with your project specs.
          </div>
        </div>
      </CardContent>
    </Card>
  );
};